import {
  ActionType,
  BridgeType,
  OnChainExecutionData,
  RouteActionResponse,
  RouteResponse,
  SquidData,
  SquidDataType,
  type ChainData,
  type StatusResponse,
} from "@0xsquid/squid-types";
import axios from "axios";
import { CHAIN_IDS, DEFAULT_LOCALE } from "../../core/constants";
import {
  HistoryTransaction,
  HistoryTxType,
  SwapTransactionHistory,
} from "../../core/types/history";
import {
  PatchedRouteActionResponse,
  SimplifiedRouteAction,
  SwapTransactionForHistoryStore,
  SwapTransactionForStatus,
  TransactionParams,
  TransactionType,
} from "../../core/types/transaction";
import { getQueryHeaders } from "./apiService";

export const formatTransactionHistoryDate = (
  transaction: SwapTransactionForHistoryStore | undefined
): { month: string; day: string } | undefined => {
  if (!transaction?.timestamp) return undefined;

  try {
    const date = new Date(Number(transaction.timestamp));

    // Format date to: MMM DD. Examples:
    // Jan 01
    // May 12
    const month = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
      month: "short",
    }).format(date);
    const day = date.toLocaleString(DEFAULT_LOCALE, { day: "2-digit" });

    return { month, day };
  } catch (error) {
    console.error("Error formatting date:", error);
    return undefined;
  }
};

export const getAxelarExplorerTxUrl = (
  urlPrefix: string | undefined,
  routeType: string | undefined,
  txID: string
): string | undefined => {
  if (!urlPrefix) {
    return undefined;
  }
  const txType = (routeType as TransactionType) ?? TransactionType.BRIDGE;
  if (
    txType === TransactionType.CALL_BRIDGE ||
    txType === TransactionType.BRIDGE
  ) {
    return `${urlPrefix}transfer/${txID}`;
  }
  return `${urlPrefix}gmp/${txID}`;
};

export const getSourceExplorerTxUrl = (
  chain: ChainData | undefined,
  txID: string
): string | undefined => {
  if (!chain || !chain.blockExplorerUrls[0] || !txID) {
    return undefined;
  }

  let txSuffix: string;

  switch (chain.chainId) {
    case CHAIN_IDS.AGORIC:
    case CHAIN_IDS.XRPL:
    case CHAIN_IDS.XRPL_TESTNET:
    case CHAIN_IDS.CANTON:
      txSuffix = "/transactions/";
      break;
    case CHAIN_IDS.HEDERA:
      txSuffix = "/transaction/";
      break;
    default:
      txSuffix = "/tx/";
  }

  if (chain.blockExplorerUrls[0].endsWith("/")) {
    txSuffix = txSuffix.slice(1);
  }

  return `${chain.blockExplorerUrls[0]}${txSuffix}${txID}`;
};

export const getMainExplorerUrl = (transaction?: TransactionParams) => {
  // The most accurate one is coming from squid /status api
  if (transaction?.statusResponse?.axelarTransactionUrl) {
    return transaction?.statusResponse.axelarTransactionUrl;
  }

  // If not, we can try to get it from the source chain
  if (transaction?.sourceTxExplorerUrl) {
    return transaction?.sourceTxExplorerUrl;
  }

  // If not, we can try to guess it from the transaction type
  if (transaction && transaction?.transactionId) {
    return getAxelarExplorerTxUrl(
      transaction.statusResponse?.axelarTransactionUrl,
      transaction.routeType,
      transaction.transactionId
    );
  }

  return undefined;
};

export const formatDistance = (
  date: number | Date,
  baseDate: number | Date,
  options?: {
    includeSeconds?: boolean | undefined;
    addSuffix?: boolean | undefined;
    locale?: Locale | undefined;
  }
): string => {
  const {
    includeSeconds = false,
    addSuffix = false,
    locale = { locale: DEFAULT_LOCALE },
  } = options || {};

  const elapsedMilliseconds = Math.abs(
    new Date(date).getTime() - new Date(baseDate).getTime()
  );

  const seconds = Math.round(elapsedMilliseconds / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);
  const months = Math.round(days / 30.44);
  const years = Math.round(days / 365.25);

  const rtf = new Intl.RelativeTimeFormat(locale.locale, { numeric: "auto" });

  let formatted = "";

  if (includeSeconds && seconds < 45) {
    const unit = addSuffix ? "second" : "seconds";
    formatted = rtf.format(-seconds, unit);
  } else if (minutes < 60) {
    formatted = rtf.format(-minutes, "minutes");
  } else if (hours < 24) {
    formatted = rtf.format(-hours, "hours");
  } else if (days < 30) {
    formatted = rtf.format(-days, "days");
  } else if (months < 12) {
    formatted = rtf.format(-months, "months");
  } else {
    formatted = rtf.format(-years, "years");
  }

  // remove "ago" from the string
  // before: "2 minutes ago"
  // after: "2 minutes"
  return addSuffix ? formatted : formatted.replace(/\b(?:ago)\b/, "").trim();
};

export const formatSeconds = (
  seconds: number,
  secondsTemplate: string = "s",
  minutesTemplate: string = "m",
  hoursTemplate: string = "h"
) => {
  let duration = "";
  if (seconds < 60) {
    duration = `${seconds.toString()}${secondsTemplate}`;
  } else {
    duration = formatDistance(0, seconds * 1000, { includeSeconds: true });
  }

  const result = duration.startsWith("1 ")
    ? duration
        .replace(" minute", minutesTemplate)
        .replace(" hour", hoursTemplate)
    : duration
        .replace(" minutes", minutesTemplate)
        .replace(" hours", hoursTemplate);

  return result;
};

/**
 * Remove the chainData from statusResponse to gain some storage space
 */
export const formatSwapTxStatusResponseForStorage = (
  sr: StatusResponse | undefined
): SwapTransactionHistory["statusResponse"] => {
  if (!sr) {
    return sr;
  }

  return {
    axelarTransactionUrl: sr.axelarTransactionUrl,
    coralTransactionUrl: sr.coralTransactionUrl,
    toChain: sr.toChain?.transactionUrl
      ? {
          transactionUrl: sr.toChain?.transactionUrl,
        }
      : undefined,
  };
};

export const simplifyRouteAction = (
  action: PatchedRouteActionResponse
): SimplifiedRouteAction => {
  return {
    type: action.type,
    provider: action.provider,
    data: {
      type: (action.data as any)?.type,
    },
    ...(action.coralV2Order ? { isCoralV2: true } : undefined),
  };
};

export const fetchSwapTransactionStatus = async ({
  transaction,
  integratorId,
  apiUrl,
}: {
  transaction?: SwapTransactionForStatus;
  integratorId: string;
  apiUrl: string;
}): Promise<StatusResponse> => {
  const statusEndpoint = `${apiUrl}/v2/status`;
  const isCanton = transaction?.fromChain === CHAIN_IDS.CANTON;
  // For Canton, the backend handles all tx detection/submission from the quoteId
  // alone, so no transactionId is sent — only the quoteId param below.
  const transactionId = isCanton
    ? undefined
    : (transaction?.transactionIdForStatus ?? transaction?.transactionId);

  try {
    const response = await axios.get<StatusResponse>(statusEndpoint, {
      params: {
        transactionId,
        fromChainId: transaction?.fromChain,
        toChainId: transaction?.toChain,
        bridgeType: transaction?.bridgeType,
        quoteId: transaction?.quoteId,
        depositTxVerificationSignature:
          transaction?.depositTxVerificationSignature,
      },
      headers: getQueryHeaders(integratorId, transaction?.quoteId),
    });

    return response.data;
  } catch (error) {
    if (error) {
      throw new Error("Fetch transaction status failed", { cause: error });
    }
    throw new Error("Fetch transaction status failed", { cause: undefined });
  }
};

export const compareTransactionIds = (idA: string, idB: string) => {
  if (!idA || !idB) {
    return false;
  }
  const normalizedA = idA.toLowerCase();
  const normalizedB = idB.toLowerCase();
  return normalizedA.includes(normalizedB) || normalizedB.includes(normalizedA);
};

interface Locale {
  locale?: string;
}

/**
 * Checks if the provided action is Coral bridge action
 */
export function isCoralBridgeAction(action: SimplifiedRouteAction): boolean {
  return (
    action.type === ActionType.RFQ &&
    // TODO: update types to use enum
    action.provider === "Squid Intents"
  );
}

/**
 * Determines if a given action completes as part of the user's source transaction.
 * Action types like RFQ are fulfilled by a solver in a separate transaction,
 * so they are NOT completed on source even if they share the same chain.
 */
export function isActionCompletedOnSourceTx(
  action: Pick<RouteActionResponse, "type" | "fromChain" | "toChain">,
  fromChainId: string
): boolean {
  if (action.type === ActionType.RFQ) return false;

  return action.fromChain === fromChainId && action.toChain === fromChainId;
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const isChainflipDepositRoute = (
  route?: RouteResponse["route"]
): boolean => {
  return (
    !!route &&
    route.transactionRequest?.type === SquidDataType.ChainflipDepositAddress
  );
};

/**
 * Checks if the route contains a Chainflip bridge action
 */
export function isChainflipBridgeTransaction(
  actions: SimplifiedRouteAction[] = []
): boolean {
  return actions.some(
    (action) =>
      action.type === ActionType.BRIDGE &&
      action.data?.type === BridgeType.CHAINFLIP
  );
}

/**
 * Checks if a route is of type {@link OnChainExecutionData}
 *
 * On-chain routes require calling a smart contract to execute
 */
export function isOnChainTxData(
  squidData: SquidData
): squidData is OnChainExecutionData {
  return [
    SquidDataType.OnChainExecution,
    SquidDataType.OnChainExecutionWithSignature,
    SquidDataType.DepositAddressWithSignature,
    SquidDataType.DepositAddressCalldata,
    SquidDataType.DepositAddressWithMemo,
  ].includes(squidData.type);
}

/**
 * Checks if a route is of type DepositAddressDirectTransfer
 */
export function isDepositAddressDirectTransferRoute(
  squidData: SquidData
): squidData is OnChainExecutionData {
  return squidData.type === SquidDataType.DepositAddressDirectTransfer;
}

export function getHistoryTransactionId(tx: HistoryTransaction) {
  switch (tx.txType) {
    case HistoryTxType.SWAP:
      return tx.data.transactionId;
    case HistoryTxType.BUY:
      return tx.data.orderId;
    case HistoryTxType.SEND:
      return tx.data.hash;
  }
}
